import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiBell, FiCheck, FiEye } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { getUserNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../services/notificationService';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';

function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { unreadCount, updateUnreadCount, setUnreadCount } = useNotifications();
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch recent notifications for dropdown preview
  const fetchRecentNotifications = async () => {
    if (!user) return;

    setLoading(true);
    
    // First try to get unread notifications
    let { data, error } = await getUserNotifications({
      limit: 5,
      read: false // Only unread notifications
    });
    
    // If no unread notifications, get recent notifications (both read and unread)
    if (!error && (!data || data.length === 0)) {
      const recentResult = await getUserNotifications({
        limit: 5
        // No read filter - get all recent notifications
      });
      data = recentResult.data;
      error = recentResult.error;
    }
    
    
    if (!error) {
      setRecentNotifications(data || []);
    } else {
      console.error('❌ Error fetching recent notifications:', error);
    }
    setLoading(false);
  };

  // Initial load and periodic updates
  useEffect(() => {
    if (user) {
      fetchRecentNotifications();

      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        if (isDropdownOpen) {
          fetchRecentNotifications();
        }
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [user, isDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.read) {
      await markNotificationAsRead(notification.id);
      updateUnreadCount(-1); // Decrease unread count by 1
      
      // Update the notification in recent list
      setRecentNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
      );
    }

    // Navigate based on notification type
    switch (notification.type) {
      case 'round_invitation':
        if (notification.round_id) {
          navigate(`/rounds`); // Navigate to rounds page where they can respond
        }
        break;
      case 'round_response':
        if (notification.round_id) {
          navigate(`/rounds`);
        }
        break;
      case 'group_member_joined':
      case 'group_member_left':
        if (notification.group_id) {
          navigate(`/group/${notification.group_id}`);
        }
        break;
      default:
        navigate('/notifications'); // Navigate to full notification center
        break;
    }

    setIsDropdownOpen(false);
  };

  // Handle mark all as read
  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    if (markingAllRead || unreadCount === 0) return;

    setMarkingAllRead(true);
    const previousCount = unreadCount;
    const previousNotifications = recentNotifications;

    setUnreadCount(0);
    setRecentNotifications(prev => prev.map(n => ({ ...n, read: true })));

    const { error } = await markAllNotificationsAsRead();
    if (error) {
      setUnreadCount(previousCount);
      setRecentNotifications(previousNotifications);
    }
    setMarkingAllRead(false);
  };

  // Handle bell click
  const handleBellClick = () => {
    // Always show dropdown first
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      fetchRecentNotifications();
    }
  };

  // Format notification time
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  // Truncate message
  const truncateMessage = (message, maxLength = 60) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleBellClick}
        className="relative p-2 text-text-secondary hover:text-text-primary transition-colors duration-200"
      >
        <SafeIcon icon={FiBell} className="w-5 h-5" />
        
        {/* Unread Count Badge */}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-1 -right-1 bg-status-error text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Dropdown */}
      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-80 bg-card rounded-xl shadow-card border border-border-subtle z-50 overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="p-4 border-b border-border-subtle">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-text-primary">Notifications</h3>
                {unreadCount > 0 && (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-text-muted">{unreadCount} new</span>
                    <button
                      onClick={handleMarkAllRead}
                      disabled={markingAllRead}
                      className="text-sm text-accent hover:underline font-medium disabled:opacity-50 min-h-[44px] sm:min-h-0 flex items-center"
                    >
                      Mark all read
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto min-h-[200px] max-h-[320px]">
              {loading ? (
                <div className="p-4 text-center">
                  <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-sm text-text-muted mt-2">Loading...</p>
                </div>
              ) : recentNotifications.length === 0 ? (
                <div className="p-4 text-center text-text-muted">
                  <SafeIcon icon={FiCheck} className="w-8 h-8 mx-auto mb-2 text-text-muted" />
                  <p className="text-sm">No recent notifications</p>
                  <p className="text-xs text-text-muted mt-1">Check the full notifications page for older items</p>
                </div>
              ) : (
                recentNotifications.map((notification) => (
                  <motion.button
                    key={notification.id}
                    whileHover={{ backgroundColor: '#f9fafb' }}
                    onClick={() => handleNotificationClick(notification)}
                    className="w-full p-4 text-left border-b border-border-subtle/50 last:border-b-0 hover:bg-surface transition-colors duration-200"
                  >
                    <div className="flex items-start space-x-3">
                      {/* Notification Icon */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        notification.priority === 'high' ? 'bg-red-50 text-status-error' :
                        notification.priority === 'medium' ? 'bg-accent/10 text-accent' :
                        'bg-surface text-text-secondary'
                      }`}>
                        <SafeIcon icon={FiBell} className="w-4 h-4" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-text-primary text-sm">
                          {notification.title}
                        </p>
                        <p className="text-text-secondary text-xs mt-1">
                          {truncateMessage(notification.message)}
                        </p>
                        <p className="text-text-muted text-xs mt-1">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>

                      {/* Unread Indicator */}
                      {!notification.read && (
                        <div className="w-2 h-2 bg-accent rounded-full flex-shrink-0 mt-2"></div>
                      )}
                    </div>
                  </motion.button>
                ))
              )}
            </div>

            {/* Footer - Always show View All link */}
            <div className="p-4 border-t border-border-subtle bg-app flex-shrink-0">
              <button
                onClick={() => {
                  navigate('/notifications');
                  setIsDropdownOpen(false);
                }}
                className="w-full text-center text-sm text-accent hover:text-accent font-medium flex items-center justify-center space-x-1 transition-colors duration-200"
              >
                <SafeIcon icon={FiEye} className="w-4 h-4" />
                <span>View All Notifications</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default NotificationBell;